import type { RealtimeTranscriptionSession } from "shared/api/transcription.api";

export interface RealtimeTranscriber {
  /** Flush the tail of the recording and resolve with the final transcript. */
  stop: () => Promise<string>;
  /** Drop the session without waiting for a final transcript. */
  close: () => void;
}

export interface RealtimeTranscriberHandlers {
  /** Called with the running transcript every time the model sends more text. */
  onText: (text: string) => void;
  /** Called when the provider reports an error mid-recording. */
  onError: () => void;
}

/**
 * How much audio the model may wait for before committing text. It is the
 * dominant part of the delay between speaking and seeing the word, and it buys
 * accuracy: more right-hand context means fewer corrections. Mistral's own demo
 * calls 240 ms "fast" and 2400 ms "slow".
 *
 * Not as low as it can go. The realtime session has no language setting — it
 * detects per stretch of audio and only reports what it decided — so a stretch
 * that is too short to place drags the transcript into another language
 * altogether, Arabic script in the middle of a Dutch sentence. Fast speech at
 * 300 ms did exactly that. This is the only knob against it.
 */
const TARGET_STREAMING_DELAY_MS = 500;

/** ~64 ms of audio per message: small enough not to sit in front of the delay above. */
const SAMPLES_PER_MESSAGE = 1024;

const HANDSHAKE_TIMEOUT_MS = 5000;

/** How long to wait for the server to acknowledge the audio format. */
const SESSION_UPDATE_TIMEOUT_MS = 2000;

/** How long to wait for `transcription.done` before settling for the deltas we have. */
const FINAL_TIMEOUT_MS = 5000;

const PROCESSOR_NAME = "pcm-capture";

/**
 * Converts the microphone's float samples to the signed 16-bit PCM the API
 * expects. It runs in the audio thread, so it is shipped as source text and
 * loaded from a blob URL rather than as a build artefact.
 */
const PROCESSOR_SOURCE = `
class PcmCaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];

    if (channel !== undefined) {
      const samples = new Int16Array(channel.length);

      for (let index = 0; index < channel.length; index += 1) {
        const sample = Math.max(-1, Math.min(1, channel[index]));
        samples[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
      }

      this.port.postMessage(samples, [samples.buffer]);
    }

    return true;
  }
}

registerProcessor(${JSON.stringify(PROCESSOR_NAME)}, PcmCaptureProcessor);
`;

/**
 * `btoa` needs a binary string, and spreading a whole buffer into
 * `String.fromCharCode` overflows the argument stack on longer chunks.
 */
const encodeBase64 = (bytes: Uint8Array): string => {
  let binary = "";

  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }

  return btoa(binary);
};

/**
 * Stream microphone audio to Mistral and report the transcript as it arrives.
 *
 * The session is passed in rather than fetched here: minting it and opening the
 * microphone both cost a few hundred milliseconds and neither needs the other,
 * so the caller starts them together.
 *
 * Rejects when the audio pipeline or the handshake fails, so the caller can
 * fall back to recording the whole clip and uploading it.
 */
export const startRealtimeTranscription = async (
  stream: MediaStream,
  session: RealtimeTranscriptionSession,
  handlers: RealtimeTranscriberHandlers,
): Promise<RealtimeTranscriber> => {
  // Ask for the model's own rate so nothing has to be resampled by hand. A
  // browser that refuses it runs the context at its own rate instead, which is
  // why the session below declares `audioContext.sampleRate` rather than 16000.
  const audioContext = new AudioContext({ sampleRate: 16000 });

  try {
    // Safari suspends a context created outside the click that opened it, and
    // awaiting the token above is enough to lose that gesture.
    await audioContext.resume();

    const processorUrl = URL.createObjectURL(
      new Blob([PROCESSOR_SOURCE], { type: "text/javascript" }),
    );

    try {
      await audioContext.audioWorklet.addModule(processorUrl);
    } finally {
      URL.revokeObjectURL(processorUrl);
    }
  } catch (error) {
    // A refused worklet is one of the documented fallback triggers, and Chrome
    // caps concurrent contexts per page — leaking one per recording would
    // eventually take the microphone down altogether.
    void audioContext.close();
    throw error;
  }

  const socket = new WebSocket(
    `${session.url}?model=${encodeURIComponent(session.model)}`,
    // Browsers cannot set an Authorization header on a WebSocket handshake, so
    // the token travels as a subprotocol (AI-REALTIME-CLIENT-TOKEN-001).
    ["realtime", session.token],
  );

  let transcript = "";
  let closed = false;
  let onSessionCreated: (() => void) | null = null;
  let onSessionUpdated: (() => void) | null = null;
  let onFailure: ((error: Error) => void) | null = null;
  let onFinalTranscript: ((text: string) => void) | null = null;

  const send = (message: Record<string, unknown>): void => {
    if (socket.readyState !== WebSocket.OPEN) {
      return;
    }

    socket.send(JSON.stringify(message));
  };

  const buffer = new Int16Array(SAMPLES_PER_MESSAGE);
  let bufferOffset = 0;

  /**
   * Audio captured before the server acknowledged the format.
   *
   * The microphone is connected as early as it can be — a teacher who clicks
   * and starts talking is speaking through the handshake — but audio appended
   * before the acknowledgement is read with the session's own default and comes
   * back as noise. So it waits here rather than being dropped or sent early.
   */
  const pendingChunks: Uint8Array[] = [];
  let isFormatAcknowledged = false;

  const appendAudio = (chunk: Uint8Array): void => {
    send({ type: "input_audio.append", audio: encodeBase64(chunk) });
  };

  const sendBuffer = (): void => {
    if (bufferOffset === 0) {
      return;
    }

    // Copied rather than viewed: `buffer` is reused for the next chunk, and a
    // pending view would encode whatever is in it by the time it is sent.
    const chunk = new Uint8Array(buffer.buffer.slice(0, bufferOffset * 2));

    bufferOffset = 0;

    if (isFormatAcknowledged === false) {
      pendingChunks.push(chunk);
      return;
    }

    appendAudio(chunk);
  };

  const pushSamples = (samples: Int16Array): void => {
    let read = 0;

    while (read < samples.length) {
      const take = Math.min(
        buffer.length - bufferOffset,
        samples.length - read,
      );

      buffer.set(samples.subarray(read, read + take), bufferOffset);
      bufferOffset += take;
      read += take;

      if (bufferOffset === buffer.length) {
        sendBuffer();
      }
    }
  };

  socket.onmessage = (event: MessageEvent): void => {
    let payload: Record<string, unknown>;

    try {
      payload = JSON.parse(String(event.data));
    } catch {
      return;
    }

    switch (payload.type) {
      case "session.created":
        onSessionCreated?.();
        break;

      case "session.updated":
        onSessionUpdated?.();
        break;

      case "transcription.text.delta":
        transcript += String(payload.text ?? "");
        handlers.onText(transcript);
        break;

      case "transcription.done": {
        const text = String(payload.text ?? "");

        // The final message is documented as the whole transcript, and the
        // deltas are what we assembled ourselves. Taking whichever is longer
        // keeps the server's punctuation without ever handing back less text
        // than the teacher already watched appear.
        if (text.length > transcript.length) {
          transcript = text;
          handlers.onText(transcript);
        }

        onFinalTranscript?.(transcript);
        break;
      }

      case "error":
        reportFailure(new Error("Realtime transcription error"));
        break;
    }
  };

  /**
   * Route a failure to whoever is waiting for one.
   *
   * Before the handshake that is the caller, which falls back to recording and
   * uploading. While stopping it just means the tail will not arrive. With
   * nobody waiting the recording is mid-flight, and `send()` would quietly drop
   * every further chunk — so the session is torn down and the teacher is told,
   * rather than left talking into a dead socket.
   */
  const reportFailure = (error: Error): void => {
    if (onFailure !== null) {
      onFailure(error);
      return;
    }

    if (onFinalTranscript !== null) {
      onFinalTranscript(transcript);
      return;
    }

    if (closed === true) {
      return;
    }

    // The close code is the only thing Mistral says about a session it drops,
    // and the caller only gets to know that something failed. Without this the
    // next report is another round of guessing.
    console.warn(error.message);

    close();
    handlers.onError();
  };

  const teardownAudio = (): void => {
    source.disconnect();
    processor.disconnect();
  };

  const close = (): void => {
    if (closed === true) {
      return;
    }

    closed = true;
    teardownAudio();
    void audioContext.close();
    socket.close();
  };

  socket.onclose = (event: CloseEvent): void => {
    // The close code is the only clue Mistral gives about a rejected
    // handshake, so it travels with the error.
    reportFailure(
      new Error(
        `Realtime transcription socket closed (${event.code} ${event.reason})`,
      ),
    );
  };

  const source = audioContext.createMediaStreamSource(stream);
  const processor = new AudioWorkletNode(audioContext, PROCESSOR_NAME);

  // Before the handshake, not after it: minting the session, opening the socket
  // and agreeing the format take the best part of a second, and everything said
  // in that window used to be lost outright.
  processor.port.onmessage = (event: MessageEvent<Int16Array>): void => {
    pushSamples(event.data);
  };

  source.connect(processor);

  try {
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error("Realtime transcription handshake timed out"));
      }, HANDSHAKE_TIMEOUT_MS);

      onSessionCreated = (): void => {
        clearTimeout(timer);
        resolve();
      };

      onFailure = (error: Error): void => {
        clearTimeout(timer);
        reject(error);
      };
    });
  } catch (error) {
    close();
    throw error;
  }

  onSessionCreated = null;
  onFailure = null;

  // Both fields are rejected once audio has started, so this has to land
  // before the first append.
  send({
    type: "session.update",
    session: {
      audio_format: {
        encoding: "pcm_s16le",
        sample_rate: audioContext.sampleRate,
      },
      target_streaming_delay_ms: TARGET_STREAMING_DELAY_MS,
    },
  });

  // Audio is only appended once the server confirms the format: sent before
  // that it is read with the session's own default and comes back as noise. The
  // microphone is already running, so what the teacher says during this wait is
  // buffered and released below rather than lost.
  //
  // A missing acknowledgement must not cost the whole recording, so this gives
  // up after a couple of seconds and streams anyway.
  await new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, SESSION_UPDATE_TIMEOUT_MS);

    onSessionUpdated = (): void => {
      clearTimeout(timer);
      resolve();
    };
  });

  onSessionUpdated = null;
  isFormatAcknowledged = true;

  // In order and before anything captured from here on: this is the opening of
  // the sentence the teacher started the moment they pressed the button.
  pendingChunks.forEach(appendAudio);
  pendingChunks.length = 0;

  const stop = async (): Promise<string> => {
    if (closed === true) {
      return transcript;
    }

    // Detach the microphone first: anything captured after the flush is
    // dropped anyway, and this stops the buffer refilling underneath us.
    teardownAudio();
    sendBuffer();

    const finalTranscript = await new Promise<string>((resolve) => {
      const timer = setTimeout(() => resolve(transcript), FINAL_TIMEOUT_MS);

      onFinalTranscript = (text: string): void => {
        clearTimeout(timer);
        resolve(text);
      };

      send({ type: "input_audio.flush" });
      send({ type: "input_audio.end" });
    });

    close();

    return finalTranscript;
  };

  return { stop, close };
};
